/**
 * Debug Store
 *
 * Manages debugging state including breakpoints on workflow nodes.
 * Used for setting breakpoints that pause execution during workflow runs.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface Breakpoint {
  /** Node name (action name) */
  nodeName: string;
  /** Whether the breakpoint is enabled */
  enabled: boolean;
  /** Optional condition expression */
  condition?: string;
  /** Hit count - pause after this many hits (0 = always pause) */
  hitCount?: number;
  /** Current number of times this breakpoint was hit */
  currentHitCount: number;
}

interface DebugState {
  /** Map of node names to breakpoints */
  breakpoints: Map<string, Breakpoint>;
  /** Whether debugging is active (breakpoints are being checked) */
  isDebuggingEnabled: boolean;
  /** Currently paused node (if any) */
  pausedAtNode: string | null;
}

interface DebugActions {
  /** Toggle a breakpoint on a node */
  toggleBreakpoint: (nodeName: string) => void;
  /** Add a breakpoint with optional condition */
  addBreakpoint: (nodeName: string, condition?: string, hitCount?: number) => void;
  /** Remove a breakpoint */
  removeBreakpoint: (nodeName: string) => void;
  /** Enable/disable a breakpoint */
  setBreakpointEnabled: (nodeName: string, enabled: boolean) => void;
  /** Update breakpoint condition */
  setBreakpointCondition: (nodeName: string, condition: string | undefined) => void;
  /** Check if a node has an active breakpoint */
  hasBreakpoint: (nodeName: string) => boolean;
  /** Check if a node has an enabled breakpoint */
  hasEnabledBreakpoint: (nodeName: string) => boolean;
  /** Toggle debugging on/off globally */
  toggleDebugging: () => void;
  /** Set paused state */
  setPausedAtNode: (nodeName: string | null) => void;
  /** Continue execution (clear paused state) */
  continueExecution: () => void;
  /** Clear all breakpoints */
  clearAllBreakpoints: () => void;
  /** Increment hit count for a breakpoint */
  incrementHitCount: (nodeName: string) => void;
  /** Reset all hit counts */
  resetHitCounts: () => void;
}

type DebugStore = DebugState & DebugActions;

export const useDebugStore = create<DebugStore>()(
  devtools(
    (set, get) => ({
      // State
      breakpoints: new Map(),
      isDebuggingEnabled: false,
      pausedAtNode: null,

      // Actions
      toggleBreakpoint: (nodeName: string) => {
        const { breakpoints } = get();
        if (breakpoints.has(nodeName)) {
          get().removeBreakpoint(nodeName);
        } else {
          get().addBreakpoint(nodeName);
        }
      },

      addBreakpoint: (nodeName: string, condition?: string, hitCount?: number) => {
        set((state) => {
          const newBreakpoints = new Map(state.breakpoints);
          const breakpoint: Breakpoint = {
            nodeName,
            enabled: true,
            currentHitCount: 0,
          };
          if (condition !== undefined) {
            breakpoint.condition = condition;
          }
          if (hitCount !== undefined) {
            breakpoint.hitCount = hitCount;
          }
          newBreakpoints.set(nodeName, breakpoint);
          return { breakpoints: newBreakpoints };
        });
      },

      removeBreakpoint: (nodeName: string) => {
        set((state) => {
          const newBreakpoints = new Map(state.breakpoints);
          newBreakpoints.delete(nodeName);
          return { breakpoints: newBreakpoints };
        });
      },

      setBreakpointEnabled: (nodeName: string, enabled: boolean) => {
        set((state) => {
          const breakpoint = state.breakpoints.get(nodeName);
          if (!breakpoint) return state;

          const newBreakpoints = new Map(state.breakpoints);
          newBreakpoints.set(nodeName, { ...breakpoint, enabled });
          return { breakpoints: newBreakpoints };
        });
      },

      setBreakpointCondition: (nodeName: string, condition: string | undefined) => {
        set((state) => {
          const breakpoint = state.breakpoints.get(nodeName);
          if (!breakpoint) return state;

          const newBreakpoints = new Map(state.breakpoints);
          const updated: Breakpoint = { ...breakpoint };
          if (condition !== undefined) {
            updated.condition = condition;
          } else {
            delete updated.condition;
          }
          newBreakpoints.set(nodeName, updated);
          return { breakpoints: newBreakpoints };
        });
      },

      hasBreakpoint: (nodeName: string) => {
        return get().breakpoints.has(nodeName);
      },

      hasEnabledBreakpoint: (nodeName: string) => {
        const breakpoint = get().breakpoints.get(nodeName);
        return breakpoint?.enabled ?? false;
      },

      toggleDebugging: () => {
        set((state) => ({ isDebuggingEnabled: !state.isDebuggingEnabled }));
      },

      setPausedAtNode: (nodeName: string | null) => {
        set({ pausedAtNode: nodeName });
      },

      continueExecution: () => {
        set({ pausedAtNode: null });
      },

      clearAllBreakpoints: () => {
        set({ breakpoints: new Map() });
      },

      incrementHitCount: (nodeName: string) => {
        set((state) => {
          const breakpoint = state.breakpoints.get(nodeName);
          if (!breakpoint) return state;

          const newBreakpoints = new Map(state.breakpoints);
          newBreakpoints.set(nodeName, {
            ...breakpoint,
            currentHitCount: breakpoint.currentHitCount + 1,
          });
          return { breakpoints: newBreakpoints };
        });
      },

      resetHitCounts: () => {
        set((state) => {
          const newBreakpoints = new Map(state.breakpoints);
          for (const [name, bp] of newBreakpoints) {
            newBreakpoints.set(name, { ...bp, currentHitCount: 0 });
          }
          return { breakpoints: newBreakpoints };
        });
      },
    }),
    { name: 'debug-store' }
  )
);

/**
 * Hook to check if a node has a breakpoint
 */
export const useNodeBreakpoint = (nodeName: string) => {
  const hasBreakpoint = useDebugStore((state) => state.breakpoints.has(nodeName));
  const isEnabled = useDebugStore((state) => state.breakpoints.get(nodeName)?.enabled ?? false);
  const isPaused = useDebugStore((state) => state.pausedAtNode === nodeName);

  return { hasBreakpoint, isEnabled, isPaused };
};
